/**
 * Overview tab's "Top ... by Absence Days/Rate" ranking — a leaderboard-style
 * list (not a chart) of the highest entities in the selected window, capped
 * at the 10 highest. A metric toggle switches what "highest" means: total
 * absence days (raw volume, naturally favors larger entities) or absence
 * rate (% of available working time lost, size-independent but noisier for
 * small groups).
 *
 * Cascades by scope level exactly like OrgTrendChart: departments by
 * default, teams once a department filter narrows it down, subteams once a
 * team filter narrows it further — so drilling into a team here shows its
 * subteams' ranking rather than repeating a single-row "team" list.
 *
 * Split into its own file (out of AbsenceInsightsCharts.tsx) to keep that
 * file under the repo's max-lines lint limit, same reasoning as
 * DayOfWeekChart.tsx and AbsenceOrgTrendChart.tsx.
 */
import { useState } from 'react';
import type { AbsenceOrgMonthlySeries, AbsenceType } from '../services/absenceApi';
import { pickDays, EmptyChartCard, TogglePill, SICK_LEAVE_COLOR, PRIOR_YEAR_COLOR, type OrgScopeLevel } from './AbsenceInsightsCharts';
import { InfoTooltip } from './InfoTooltip';

const MAX_ITEMS = 10;

type RankMetric = 'days' | 'rate';

const HEADING_BY_LEVEL: Record<OrgScopeLevel, string> = {
  department: 'Top Departments by Absence',
  team: 'Top Teams by Absence',
  subteam: 'Top Subteams by Absence',
};

interface EntityTotal {
  key: string;
  primary: string;
  context: string;
  totalDays: number;
  ratePct: number;
  // True when at least one month in the window was GDPR-suppressed for this
  // entity (fewer than 5 employees that month) — totalDays/ratePct only sum
  // the visible months, so this flags both as a possible undercount rather
  // than silently presenting them as complete.
  partiallyHidden: boolean;
}

/** Same cascade as orgEntityLabel/orgEntityKey in AbsenceOrgTrendChart.tsx — kept local since the "context" breadcrumb differs slightly (always shown here, not just used for a chart legend label). */
function entityTotal(entity: AbsenceOrgMonthlySeries, level: OrgScopeLevel, absenceType: AbsenceType): EntityTotal {
  const primary =
    level === 'subteam' ? entity.subteam ?? entity.department : level === 'team' ? entity.team ?? entity.department : entity.department;
  const context =
    level === 'subteam'
      ? [entity.department, entity.team].filter(Boolean).join(' / ')
      : level === 'team'
        ? entity.department
        : '';

  let totalDays = 0;
  let totalAvailableWorkingDays = 0;
  for (const p of entity.points) {
    totalDays += pickDays(p, absenceType) ?? 0;
    totalAvailableWorkingDays += p.availableWorkingDays ?? 0;
  }
  // Same formula as pickRatePct/ratePct elsewhere — summed days over summed
  // available working days across the whole range, not an average of each
  // month's own rate (which would misweight months with different headcounts).
  const ratePct = totalAvailableWorkingDays > 0 ? Math.round((totalDays / totalAvailableWorkingDays) * 10000) / 100 : 0;

  return {
    key: [entity.department, entity.team, entity.subteam].filter(Boolean).join('||'),
    primary,
    context,
    totalDays,
    ratePct,
    partiallyHidden: entity.points.some((p) => p.suppressed),
  };
}

/** Compact inline YoY delta — same colour/arrow convention as the KPI cards' YoyBadge, sized down for a list row rather than a card. */
function DeltaBadge({ current, prior, format = 'count' }: { current: number; prior: number; format?: 'count' | 'pct' }) {
  const delta = current - prior;
  const neutral = Math.abs(delta) < (format === 'pct' ? 0.05 : 0.5);
  const better = delta < 0; // fewer absence days/lower rate is better
  const colorClass = neutral ? 'bg-cotton text-iron' : better ? 'bg-lightGreen text-green' : 'bg-lightRed text-red';
  const arrow = neutral ? '→' : delta > 0 ? '↑' : '↓';
  const sign = delta > 0 ? '+' : '';
  const deltaStr = format === 'pct' ? `${sign}${delta.toFixed(1)}pp` : `${sign}${Math.round(delta)}`;
  return (
    <span className={`rounded px-1 py-0.5 text-[10px] font-semibold ${colorClass}`}>
      {arrow} {deltaStr}
    </span>
  );
}

export function AbsenceRankingList({
  currentByDept,
  priorByDept,
  currentByTeam,
  priorByTeam,
  currentBySubteam,
  priorBySubteam,
  scopeLevel,
  absenceType,
}: {
  currentByDept: AbsenceOrgMonthlySeries[];
  priorByDept: AbsenceOrgMonthlySeries[];
  currentByTeam: AbsenceOrgMonthlySeries[];
  priorByTeam: AbsenceOrgMonthlySeries[];
  currentBySubteam: AbsenceOrgMonthlySeries[];
  priorBySubteam: AbsenceOrgMonthlySeries[];
  scopeLevel: OrgScopeLevel;
  absenceType: AbsenceType;
}) {
  const [showPrior, setShowPrior] = useState(false);
  const [metric, setMetric] = useState<RankMetric>('days');

  const heading = HEADING_BY_LEVEL[scopeLevel];
  const entities =
    scopeLevel === 'subteam' ? currentBySubteam : scopeLevel === 'team' ? currentByTeam : currentByDept;
  const priorEntities =
    scopeLevel === 'subteam' ? priorBySubteam : scopeLevel === 'team' ? priorByTeam : priorByDept;

  if (entities.length === 0) {
    return <EmptyChartCard title={heading} message="No data in the selected window." />;
  }

  const metricValue = (t: EntityTotal) => (metric === 'rate' ? t.ratePct : t.totalDays);

  const totals = entities.map((entity) => entityTotal(entity, scopeLevel, absenceType));
  const ranked = [...totals].sort((a, b) => metricValue(b) - metricValue(a)).slice(0, MAX_ITEMS);
  const maxValue = Math.max(...ranked.map(metricValue), 0);
  const anyHidden = ranked.some((t) => t.partiallyHidden);

  // Matched by entity key (department/team/subteam), not re-ranked — this
  // shows what THIS YEAR's top entities looked like a year ago, same
  // convention as every other "Compare previous year" toggle in this
  // dashboard (OrgTrendChart, TenureChart, etc.), rather than an
  // independent prior-year top-10.
  const priorByKey = new Map(
    priorEntities.map((entity) => [
      [entity.department, entity.team, entity.subteam].filter(Boolean).join('||'),
      entityTotal(entity, scopeLevel, absenceType),
    ])
  );
  const anyPriorHidden = showPrior && ranked.some((t) => priorByKey.get(t.key)?.partiallyHidden);

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
            {heading}
            <InfoTooltip text="The department/team/subteam entities ranked highest to lowest by the selected metric (top 10). Days measures raw volume — larger entities naturally accumulate more. Rate measures % of available working time lost, which is size-independent but noisier for small groups." />
          </h2>
          <p className="mt-1 text-xs text-zinc-400">
            Highest to lowest {metric === 'rate' ? 'absence rate' : 'total absence days'} — top {MAX_ITEMS}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <TogglePill active={metric === 'days'} onClick={() => setMetric('days')}>
              Days
            </TogglePill>
            <TogglePill active={metric === 'rate'} onClick={() => setMetric('rate')}>
              Rate
            </TogglePill>
          </div>
          <TogglePill active={showPrior} onClick={() => setShowPrior((v) => !v)}>
            Compare previous year
          </TogglePill>
        </div>
      </div>
      <ol className="mt-4 flex flex-col gap-2.5">
        {ranked.map((t, i) => {
          // A visible sliver even at a low nonzero value, so the bar never
          // reads as "no data" when the entity simply isn't the top of the pack.
          const value = metricValue(t);
          const widthPct = maxValue > 0 ? Math.max((value / maxValue) * 100, value > 0 ? 4 : 0) : 0;
          const prior = priorByKey.get(t.key);
          const displayValue = (x: EntityTotal) => (metric === 'rate' ? `${x.ratePct.toFixed(1)}%` : x.totalDays.toLocaleString());
          return (
            <li key={t.key} className="flex items-center gap-3">
              <span className="w-5 shrink-0 text-right text-xs font-semibold tabular-nums text-zinc-400">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium text-zinc-800">
                    {t.primary}
                    {t.context && <span className="text-xs text-zinc-400"> · {t.context}</span>}
                  </span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-zinc-900">
                    {displayValue(t)}
                    {t.partiallyHidden && (
                      <span title="Some months hidden for privacy reasons — total may be an undercount">*</span>
                    )}
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-zinc-100">
                  <div className="h-1.5 rounded-full" style={{ width: `${widthPct}%`, backgroundColor: SICK_LEAVE_COLOR }} />
                </div>
                {showPrior && (
                  <div className="mt-1 flex items-center gap-1.5 text-[11px]">
                    <span style={{ color: PRIOR_YEAR_COLOR }}>Last year:</span>
                    {prior ? (
                      <>
                        <span className="font-medium tabular-nums text-zinc-500">
                          {displayValue(prior)}
                          {prior.partiallyHidden && (
                            <span title="Some months hidden for privacy reasons — total may be an undercount">*</span>
                          )}
                        </span>
                        <DeltaBadge current={value} prior={metricValue(prior)} format={metric === 'rate' ? 'pct' : 'count'} />
                      </>
                    ) : (
                      <span className="italic text-zinc-400">No prior-year data</span>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {(anyHidden || anyPriorHidden) && (
        <p className="mt-3 text-[10px] text-zinc-400">
          * Some months hidden for privacy reasons (fewer than 5 employees) — total may be an undercount.
        </p>
      )}
    </section>
  );
}
